import { Col, Row } from "antd";
import dayjs from "dayjs";
import { get } from "lodash";
import { useState } from "react";
import { useTranslation } from "react-i18next";
import {
  MdOutlineBadge,
  MdOutlineCake,
  MdOutlineHealthAndSafety,
  MdOutlineSubtitles,
  MdOutlineWorkOutline,
  MdPeopleOutline,
} from "react-icons/md";
import Form from "shared/components/Form";
import FormField from "shared/components/FormField";
import Input from "shared/components/Input";
import Select from "shared/components/Select";
import { NAME_SPACES } from "shared/locales/constants";
import {
  DATE_FORMAT,
  GENDER,
  MILTIS_SOCIAL_REGIMES,
  RELATIONSHIP,
  SOCIO_PROFESSIONAL_CATEGORIES,
  SUBSCRIPTION_FORM_KEYS,
} from "utils/constants";
import { getLastname, hasSameLastname } from "utils/helpers/sameLastname";

const ADHERENT_LASTNAME_PATH = "project.contact.user.lastname";

const Partner = ({ setPercent, isFieldRequired, bindField, initialValues }) => {
  const { t } = useTranslation(NAME_SPACES.PRIVATE.BROKER.CONTRACT);
  const SUBSCRIPTION = t("SUBSCRIPTION", { returnObjects: true });
  const { t: p } = useTranslation(NAME_SPACES.PUBLIC.PROCESSES);
  const SOCIAL_REGIMES_TRANSLATION = p("B2C_MILTIS.SOCIAL_REGIMES", {
    returnObjects: true,
  });
  const birthDate = get(
    initialValues,
    "spouse.identity.user.birthDate",
    undefined,
  );

  const genderPath = "spouse.identity.user.gender";
  const genderField = bindField(genderPath, {
    label: SUBSCRIPTION.FORM.GENDER.LABEL,
    required: isFieldRequired(genderPath),
    onChange: () => setPercent(initialValues),
  });

  const lastnamePath = "spouse.identity.user.lastname";
  const lastnameField = bindField(lastnamePath, {
    label: SUBSCRIPTION.FORM.LAST_NAME.LABEL,
    required: isFieldRequired(lastnamePath),
    onBlur: () => setPercent(initialValues),
  });
  const [sameLastname, setSameLastname] = useState(() =>
    hasSameLastname(initialValues, lastnamePath, ADHERENT_LASTNAME_PATH),
  );
  const [lastnameVersion, setLastnameVersion] = useState(0);
  const onSameLastnameChange = (checked) => {
    const adherentLastname = getLastname(initialValues, ADHERENT_LASTNAME_PATH);
    if (checked && !adherentLastname) return;
    setSameLastname(checked);
    if (!checked) return;
    lastnameField.onChange(adherentLastname);
    setLastnameVersion((version) => version + 1);
    setPercent(initialValues);
  };

  const birthNamePath = "spouse.identity.user.birthName";
  const birthNameField = bindField(birthNamePath, {
    label: SUBSCRIPTION.FORM.BIRTH_NAME.LABEL,
    required: isFieldRequired(birthNamePath),
    onBlur: () => setPercent(initialValues),
  });

  const firstnamePath = "spouse.identity.user.firstname";
  const firstnameField = bindField(firstnamePath, {
    label: SUBSCRIPTION.FORM.FIRST_NAME.LABEL,
    required: isFieldRequired(firstnamePath),
    onBlur: () => setPercent(initialValues),
  });

  const socioProfessionalCategoryPath =
    "spouse.identity.socioProfessionalCategory";
  const socioProfessionalCategoryField = bindField(
    socioProfessionalCategoryPath,
    {
      label: SUBSCRIPTION.FORM.SOCIO_PROFESSIONAL_CATEGORY.LABEL,
      required: isFieldRequired(socioProfessionalCategoryPath),
      onBlur: () => setPercent(initialValues),
    },
  );

  const socialRegimePath = "spouse.identity.socialRegime";
  const socialRegimeField = bindField(socialRegimePath, {
    label: SUBSCRIPTION.FORM.MANDATORY_SCHEME.LABEL,
    required: isFieldRequired(socialRegimePath),
    onChange: () => setPercent(initialValues),
    onBlur: () => setPercent(initialValues),
  });

  const socialSecurityNumberPath = "spouse.identity.socialSecurityNumber";
  const socialSecurityNumberField = bindField(socialSecurityNumberPath, {
    label: SUBSCRIPTION.FORM.SOCIAL_SECURITY_NUMBER.LABEL,
    required: isFieldRequired(socialSecurityNumberPath),
    onBlur: () => setPercent(initialValues),
  });

  return (
    get(initialValues, "project.contact.relatedPersons", []).find(
      (person) => person.relationship === RELATIONSHIP.SPOUSE,
    ) && (
      <div
        className="subscription-form--wrapper"
        id={`${SUBSCRIPTION_FORM_KEYS.ADDITIONAL_INFORMATION.PARTNER}-section`}
      >
        <Form
          type="vertical"
          title={SUBSCRIPTION.FORM.ADDITIONAL_INFORMATION.PARTNER.TITLE}
          subtitle={SUBSCRIPTION.FORM.ADDITIONAL_INFORMATION.PARTNER.SUBTITLE}
        >
          <Row gutter={[16, 16]}>
            <Col lg={24} xs={24}>
              <FormField
                path={genderField.path}
                label={genderField.label}
                required={genderField.required}
                disabled={genderField.disabled}
              >
                {(field) => (
                  <Input
                    type="radio"
                    variant="group"
                    icon={<MdPeopleOutline icon="man" size={16} />}
                    values={[
                      {
                        value: GENDER.MALE,
                        label: SUBSCRIPTION.FORM.GENDER.OPTIONS.MR,
                      },
                      {
                        value: GENDER.FEMALE,
                        label: SUBSCRIPTION.FORM.GENDER.OPTIONS.MRS,
                      },
                    ]}
                    label={genderField.label}
                    value={get(initialValues, genderPath)}
                    {...field}
                    onChange={genderField.onChange}
                    onBlur={genderField.onBlur}
                  />
                )}
              </FormField>
            </Col>
            <Col lg={24} xs={24}>
              <Input
                type="checkbox"
                id="same-lastname-spouse"
                label={SUBSCRIPTION.FORM.SAME_LASTNAME_AS_ADHERENT}
                checked={sameLastname}
                onChange={({ target }) => onSameLastnameChange(target.checked)}
              />
            </Col>
            <Col lg={24} xs={24}>
              <FormField
                path={lastnameField.path}
                label={lastnameField.label}
                required={lastnameField.required}
                disabled={lastnameField.disabled}
              >
                {(field) => (
                  <Input
                    key={`${lastnamePath}-${lastnameVersion}`}
                    placeholder={SUBSCRIPTION.FORM.LAST_NAME.PLACEHOLDER}
                    label={lastnameField.label}
                    icon={<MdOutlineBadge size={16} />}
                    type="custom"
                    normalizeText={true}
                    defaultValue={lastnameField.defaultValue}
                    {...field}
                    onChange={lastnameField.onChange}
                    onBlur={lastnameField.onBlur}
                  />
                )}
              </FormField>
            </Col>
            <Col lg={24} xs={24}>
              <FormField
                path={birthNameField.path}
                label={birthNameField.label}
                required={birthNameField.required}
                disabled={birthNameField.disabled}
              >
                {(field) => (
                  <Input
                    placeholder={SUBSCRIPTION.FORM.BIRTH_NAME.PLACEHOLDER}
                    label={birthNameField.label}
                    icon={<MdOutlineBadge size={16} />}
                    type="custom"
                    normalizeText={true}
                    defaultValue={birthNameField.defaultValue}
                    {...field}
                    onChange={birthNameField.onChange}
                    onBlur={birthNameField.onBlur}
                    optional={!birthNameField.required}
                  />
                )}
              </FormField>
            </Col>
            <Col lg={24} xs={24}>
              <FormField
                path={firstnameField.path}
                label={firstnameField.label}
                required={firstnameField.required}
                disabled={firstnameField.disabled}
              >
                {(field) => (
                  <Input
                    label={firstnameField.label}
                    icon={<MdOutlineBadge size={16} />}
                    placeholder={SUBSCRIPTION.FORM.FIRST_NAME.PLACEHOLDER}
                    type="custom"
                    normalizeText={true}
                    defaultValue={firstnameField.defaultValue}
                    {...field}
                    onChange={firstnameField.onChange}
                    onBlur={firstnameField.onBlur}
                  />
                )}
              </FormField>
            </Col>
            <Col lg={24} xs={24}>
              <Input
                label={SUBSCRIPTION.FORM.BIRTH_DATE.LABEL}
                icon={<MdOutlineCake size={16} />}
                placeholder={SUBSCRIPTION.FORM.BIRTH_DATE.PLACEHOLDER}
                defaultValue={birthDate && dayjs(birthDate)}
                type="date"
                variant="simple"
                format={DATE_FORMAT}
                disabled
                required={isFieldRequired("spouse.identity.user.birthDate")}
              />
            </Col>
            <Col lg={24} xs={24}>
              <FormField
                path={socioProfessionalCategoryField.path}
                label={socioProfessionalCategoryField.label}
                required={socioProfessionalCategoryField.required}
                disabled={socioProfessionalCategoryField.disabled}
              >
                {(field) => (
                  <Select
                    type="simple"
                    optional={SUBSCRIPTION.FORM.OPTIONAL}
                    label={socioProfessionalCategoryField.label}
                    icon={<MdOutlineWorkOutline size={16} />}
                    placeholder={
                      SUBSCRIPTION.FORM.SOCIO_PROFESSIONAL_CATEGORY.PLACEHOLDER
                    }
                    options={Object.entries(SOCIO_PROFESSIONAL_CATEGORIES).map(
                      ([key, value]) => ({
                        value,
                        label:
                          SUBSCRIPTION.FORM.SOCIO_PROFESSIONAL_CATEGORY.OPTIONS[
                            key
                          ],
                      }),
                    )}
                    defaultValue={socioProfessionalCategoryField.defaultValue}
                    {...field}
                    onChange={socioProfessionalCategoryField.onChange}
                    onBlur={socioProfessionalCategoryField.onBlur}
                  />
                )}
              </FormField>
            </Col>
            <Col lg={24} xs={24}>
              <FormField
                path={socialRegimeField.path}
                label={socialRegimeField.label}
                required={socialRegimeField.required}
                disabled={socialRegimeField.disabled}
              >
                {(field) => (
                  <Select
                    icon={<MdOutlineHealthAndSafety size={16} />}
                    options={Object.values(MILTIS_SOCIAL_REGIMES).map(
                      (option) => {
                        const key = Object.keys(MILTIS_SOCIAL_REGIMES).find(
                          (k) => MILTIS_SOCIAL_REGIMES[k] === option,
                        );
                        return {
                          label: SOCIAL_REGIMES_TRANSLATION[key] || option,
                          value: option,
                        };
                      },
                    )}
                    label={socialRegimeField.label}
                    placeholder={SUBSCRIPTION.FORM.MANDATORY_SCHEME.PLACEHOLDER}
                    defaultValue={socialRegimeField.defaultValue}
                    {...field}
                    onChange={socialRegimeField.onChange}
                    onBlur={socialRegimeField.onBlur}
                  />
                )}
              </FormField>
            </Col>
            <Col lg={24} xs={24}>
              <FormField
                path={socialSecurityNumberField.path}
                label={socialSecurityNumberField.label}
                required={socialSecurityNumberField.required}
                disabled={socialSecurityNumberField.disabled}
              >
                {(field) => (
                  <Input
                    label={socialSecurityNumberField.label}
                    icon={<MdOutlineSubtitles size={16} />}
                    placeholder={
                      SUBSCRIPTION.FORM.SOCIAL_SECURITY_NUMBER.PLACEHOLDER
                    }
                    type="custom"
                    trim={true}
                    defaultValue={socialSecurityNumberField.defaultValue}
                    {...field}
                    onChange={socialSecurityNumberField.onChange}
                    onBlur={socialSecurityNumberField.onBlur}
                  />
                )}
              </FormField>
            </Col>
          </Row>
        </Form>
      </div>
    )
  );
};

export default Partner;
